// src/components/SugerirCandidatoModal.js
import React, { useEffect, useMemo, useState } from "react";
import "./SugerirCandidatoModal.css";
import { db } from "../firebase";

// Serviços
import { getSugestaoConfig } from "../services/sugerirService";
import {
  getCategoriasOrdenadasDoModo,
  getCamposParaCategoria,
  getNomeBonito,
  getTipoCategoria,
} from "../services/sugerirSchemas";

// Firestore
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  increment,
  arrayUnion,
} from "firebase/firestore";

// Ícones dentro do modal
const ICONS = {
  sugerir: "https://i.imgur.com/dff12HS.png",
  votarOff: "https://i.imgur.com/3ztediY.png",
  votarOn: "https://i.imgur.com/7uKqqaS.png",
  aprovadoVerde: "https://i.imgur.com/7uKqqaS.png",
  aprovadoAmarelo: "https://i.imgur.com/awSsokO.png",
  defaultImg: "https://i.imgur.com/eOjYRf6.png",
};

const PREMIACOES_LABEL = {
  oscar: "Oscar",
  globodeouro: "Globo de Ouro",
  bafta: "BAFTA",
  criticschoice: "Critics Choice",
  sagawards: "SAG Awards",
  pga: "PGA",
  dga: "DGA",
};

const PAGE_SIZE = 10;

export default function SugerirCandidatoModal({ isOpen, onClose, currentUid }) {
  const [tab, setTab] = useState("votacao"); // 'votacao' | 'aprovado'
  const [config, setConfig] = useState(null);

  // Listas + paginação
  const [listaVotacao, setListaVotacao] = useState([]);
  const [votaLoading, setVotaLoading] = useState(false);
  const [votaHasMore, setVotaHasMore] = useState(false);
  const [votaCursor, setVotaCursor] = useState(null);

  const [listaAprovados, setListaAprovados] = useState([]);
  const [aprovLoading, setAprovLoading] = useState(false);
  const [aprovHasMore, setAprovHasMore] = useState(false);
  const [aprovCursor, setAprovCursor] = useState(null);

  const [abrirForm, setAbrirForm] = useState(false);

  // popup de mensagem simples
  const [msgOpen, setMsgOpen] = useState(false);
  const [msgTitle, setMsgTitle] = useState("");
  const [msgText, setMsgText] = useState("");

  // cache do meu perfil (p/ voto especialista) + bloqueio de clique duplo
  const [euEspecialista, setEuEspecialista] = useState(null);
  const [votandoIds, setVotandoIds] = useState(new Set());

  // Lê config 1x quando o modal abre
  useEffect(() => {
    if (!isOpen) return;
    let ativo = true;
    (async () => {
      try {
        const cfg = await getSugestaoConfig(db); // 1 leitura ao abrir
        if (!ativo) return;
        setConfig(cfg);
      } catch {
        setConfig(null);
      }
    })();
    return () => {
      ativo = false;
      // reset ao fechar
      setListaVotacao([]); setVotaCursor(null); setVotaHasMore(false); setVotaLoading(false);
      setListaAprovados([]); setAprovCursor(null); setAprovHasMore(false); setAprovLoading(false);
      setTab("votacao");
      setMsgOpen(false); setMsgTitle(""); setMsgText("");
      setEuEspecialista(null);
      setVotandoIds(new Set());
    };
  }, [isOpen]);

  // Carregar aba “Em votação” quando exibida
  useEffect(() => {
    if (!isOpen || tab !== "votacao" || !config?.enabled) return;
    if (listaVotacao.length > 0 || votaLoading) return;
    loadVotacao(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, isOpen, config?.enabled]);

  // Carregar aba “Aprovado” quando exibida
  useEffect(() => {
    if (!isOpen || tab !== "aprovado" || !config?.enabled) return;
    if (listaAprovados.length > 0 || aprovLoading) return;
    loadAprovados(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, isOpen, config?.enabled]);

  async function loadVotacao(initial = false) {
    if (votaLoading) return;
    setVotaLoading(true);
    try {
      const col = collection(db, "sugestoes");
      const base = [
        where("status", "==", "em_votacao"),
        orderBy("votosCount", "desc"),
        orderBy("createdAt", "asc"),
        limit(PAGE_SIZE),
      ];
      const qy = initial || !votaCursor ? query(col, ...base) : query(col, ...base, startAfter(votaCursor));
      const snap = await getDocs(qy); // 1 leitura por página
      const docs = snap.docs || [];
      const pageItems = docs.map((d) => {
        const data = d.data() || {};
        const votosNec = typeof data.votosNecessarios === "number" && data.votosNecessarios > 0
          ? data.votosNecessarios
          : (config?.votosPadrao || 40);
        const votos = Number(data.votosCount || 0);
        const faltam = Math.max(0, votosNec - votos);
        return {
          id: d.id,
          imagemUrl: data.imagemUrl || "",
          sugeridoPor: data.sugeridoPor || { nome: "—" },
          titulo: data.titulo || "",
          filme: data.filme || "",
          premiacaoId: data.premiacaoId || "",
          categoriaId: data.categoriaId || "",
          votosCount: votos,
          votosNecessarios: votosNec,
          faltam,
          jaVotei: Array.isArray(data.voters)
            ? data.voters.includes(currentUid || window?.__uid || "")
            : false,
        };
      });

      setListaVotacao((cur) => (initial ? pageItems : [...cur, ...pageItems]));
      if (docs.length === PAGE_SIZE) {
        setVotaHasMore(true);
        setVotaCursor(docs[docs.length - 1]);
      } else {
        setVotaHasMore(false);
        setVotaCursor(null);
      }
    } finally {
      setVotaLoading(false);
    }
  }

  async function loadAprovados(initial = false) {
    if (aprovLoading) return;
    setAprovLoading(true);
    try {
      const col = collection(db, "sugestoes");
      const base = [
        where("status", "==", "aprovado"),
        orderBy("aprovadoAt", "desc"),
        limit(PAGE_SIZE),
      ];
      const qy = initial || !aprovCursor ? query(col, ...base) : query(col, ...base, startAfter(aprovCursor));
      const snap = await getDocs(qy);
      const docs = snap.docs || [];
      const pageItems = docs.map((d) => {
        const data = d.data() || {};
        const votosNec = typeof data.votosNecessarios === "number" && data.votosNecessarios > 0
          ? data.votosNecessarios
          : (config?.votosPadrao || 40);
        const votos = Number(data.votosCount || 0);
        return {
          id: d.id,
          imagemUrl: data.imagemUrl || "",
          sugeridoPor: data.sugeridoPor || { nome: "—", especialista: false },
          titulo: data.titulo || "",
          filme: data.filme || "",
          premiacaoId: data.premiacaoId || "",
          categoriaId: data.categoriaId || "",
          votosCount: votos,
          votosNecessarios: votosNec,
          aprovadoPor: data.aprovadoPor || "votos", // "votos" | "especialistaAutor" | "especialistaVoto"
        };
      });

      setListaAprovados((cur) => (initial ? pageItems : [...cur, ...pageItems]));
      if (docs.length === PAGE_SIZE) {
        setAprovHasMore(true);
        setAprovCursor(docs[docs.length - 1]);
      } else {
        setAprovHasMore(false);
        setAprovCursor(null);
      }
    } finally {
      setAprovLoading(false);
    }
  }

  async function ensureEuEspecialista(uid) {
    if (euEspecialista !== null) return euEspecialista;
    try {
      const uref = doc(db, "usuarios", uid || (window.__uid || "anon"));
      const usnap = await getDoc(uref);
      const ok = usnap.exists() && !!(usnap.data() || {}).especialista;
      setEuEspecialista(ok);
      return ok;
    } catch {
      setEuEspecialista(false);
      return false;
    }
  }

  async function handleVotar(item) {
    if (!currentUid) return;
    if (item.jaVotei) return;
    if (votandoIds.has(item.id)) return;
    const novoSet = new Set(votandoIds); novoSet.add(item.id); setVotandoIds(novoSet);

    const souEsp = await ensureEuEspecialista(currentUid);

    // otimista
    const countAntes = Number(item.votosCount || 0);
    const countDepois = countAntes + 1;
    const atingiuLimiar = countDepois >= (item.votosNecessarios || 40);

    setListaVotacao((cur) => {
      const arr = Array.isArray(cur) ? [...cur] : [];
      const idx = arr.findIndex((x) => x.id === item.id);
      if (idx >= 0) {
        arr[idx] = {
          ...arr[idx],
          jaVotei: true,
          votosCount: countDepois,
          faltam: Math.max(0, (item.votosNecessarios || 40) - countDepois),
        };
      }
      return arr;
    });

    const sugRef = doc(db, "sugestoes", item.id);
    const update = {
      voters: arrayUnion(currentUid),
      votosCount: increment(1),
    };

    if (souEsp) {
      update.status = "aprovado";
      update.aprovadoPor = "especialistaVoto";
      update.aprovadoAt = serverTimestamp();
    } else if (atingiuLimiar) {
      update.status = "aprovado";
      update.aprovadoPor = "votos";
      update.aprovadoAt = serverTimestamp();
    }

    try {
      await updateDoc(sugRef, update);

      if (souEsp || atingiuLimiar) {
        const aprovadoItem = {
          ...item,
          votosCount: countDepois,
          aprovadoPor: souEsp ? "especialistaVoto" : "votos",
        };
        setListaVotacao((cur) => (Array.isArray(cur) ? cur.filter((x) => x.id !== item.id) : []));
        setListaAprovados((cur) => [aprovadoItem, ...(Array.isArray(cur) ? cur : [])]);
      }
    } catch (e) {
      console.error("Falha ao votar:", e);
      // reverte otimista
      setListaVotacao((cur) => {
        const arr = Array.isArray(cur) ? [...cur] : [];
        const idx = arr.findIndex((x) => x.id === item.id);
        if (idx >= 0) {
          arr[idx] = {
            ...arr[idx],
            jaVotei: false,
            votosCount: countAntes,
            faltam: Math.max(0, (item.votosNecessarios || 40) - countAntes),
          };
        }
        return arr;
      });
    } finally {
      setVotandoIds((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  }

  function renderLinha(item, aprovado = false) {
    const nomePrem = PREMIACOES_LABEL[item.premiacaoId] || item.premiacaoId;
    const nomeCat = getNomeBonito(item.categoriaId);
    const img = item.imagemUrl || ICONS.defaultImg;

    return (
      <div className="scm-row" key={item.id}>
        <div className="scm-col img">
          <img src={img} alt="" />
        </div>
        <div className="scm-col info-esq">
          <div className="scm-sugerido">SUGERIDO POR: {item.sugeridoPor?.nome || "—"}</div>
          <div className="scm-titulo">{item.titulo}</div>
          {item.filme ? <div className="scm-sub">{item.filme}</div> : null}
        </div>
        <div className="scm-col info-meio">
          <div className="scm-cat">{nomeCat}</div>
          <div className="scm-prem">INDICADOS AO {nomePrem?.toUpperCase?.()}</div>
        </div>
        <div className="scm-col acao">
          {aprovado ? (
            <div className="scm-aprovado-badge">
              <img
                src={item.aprovadoPor === "votos" ? ICONS.aprovadoVerde : ICONS.aprovadoAmarelo}
                alt="aprovado"
              />
              <div className="scm-aprovado-text">
                {item.aprovadoPor === "votos" && (
                  <>
                    <strong>{item.votosCount}/{item.votosNecessarios}</strong>
                    <span>Aprovado</span>
                  </>
                )}
                {item.aprovadoPor === "especialistaAutor" && (
                  <>
                    <strong>{item.votosCount}/{item.votosNecessarios}</strong>
                    <span>Aprovado por ser sugerido por especialista</span>
                  </>
                )}
                {item.aprovadoPor === "especialistaVoto" && (
                  <>
                    <strong>{item.votosCount}/{item.votosNecessarios}</strong>
                    <span>Votado para aprovação por especialista</span>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="scm-voto-wrap">
              <button
                className={`scm-votar ${item.jaVotei ? "ja" : ""}`}
                disabled={item.jaVotei || votandoIds.has(item.id)}
                onClick={() => handleVotar(item)}
                aria-label={item.jaVotei ? "Você já votou" : "Votar"}
              >
                <img src={item.jaVotei ? ICONS.votarOn : ICONS.votarOff} alt="" />
              </button>
              <div className="scm-votos">
                <div className="scm-votos-title">APROVAÇÃO</div>
                <div className="scm-votos-num">
                  <strong>{item.votosCount}/{item.votosNecessarios}</strong>
                </div>
                {typeof item.faltam === "number" && (
                  <div className="scm-votos-rest">FALTAM {item.faltam} VOTOS</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ======= RENDER =======
  if (!isOpen) return null;

  return (
    <div className="scm-wrap" role="dialog" aria-modal="true">
      <button className="scm-backdrop" onClick={onClose} aria-label="Fechar" />
      <div className="scm-modal">
        <header className="scm-head">
          <img src={ICONS.sugerir} alt="" className="scm-ico" />
          <h2>SUGERIR CANDIDATO</h2>
          <button className="scm-x" onClick={onClose} aria-label="Fechar">×</button>
        </header>

        {!config?.enabled ? (
          <div className="scm-disabled">
            <p>Função desativada no momento.</p>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="scm-tabs">
              <button
                className={`scm-tab ${tab === "votacao" ? "active" : ""}`}
                onClick={() => setTab("votacao")}
              >
                EM VOTAÇÃO
              </button>
              <button
                className={`scm-tab ${tab === "aprovado" ? "active" : ""}`}
                onClick={() => setTab("aprovado")}
              >
                APROVADO
              </button>
            </div>

            {/* Cabeçalho da lista */}
            <div className="scm-list-head">
              <div className="esq">SUGESTÃO</div>
              <div className="dir">{tab === "votacao" ? "VOTE" : "APROVADO"}</div>
            </div>

            {/* Lista */}
            <div className="scm-list">
              {tab === "votacao" && (
                <>
                  {votaLoading && listaVotacao.length === 0 && <div className="scm-spinner" aria-label="Carregando" />}
                  {!votaLoading && listaVotacao.map((it) => renderLinha(it, false))}
                  {!votaLoading && listaVotacao.length === 0 && <div className="scm-empty">Nenhuma sugestão por aqui.</div>}
                  {listaVotacao.length > 0 && (
                    <div className="scm-pager">
                      <button
                        className="scm-btn-more"
                        onClick={() => loadVotacao(false)}
                        disabled={votaLoading || !votaHasMore}
                        title={votaHasMore ? "Carregar mais" : "Não há mais itens"}
                      >
                        {votaLoading ? "Carregando…" : (votaHasMore ? "VER MAIS" : "FIM DA LISTA")}
                      </button>
                    </div>
                  )}
                </>
              )}

              {tab === "aprovado" && (
                <>
                  {aprovLoading && listaAprovados.length === 0 && <div className="scm-spinner" aria-label="Carregando" />}
                  {!aprovLoading && listaAprovados.map((it) => renderLinha(it, true))}
                  {!aprovLoading && listaAprovados.length === 0 && <div className="scm-empty">Nenhum aprovado ainda.</div>}
                  {listaAprovados.length > 0 && (
                    <div className="scm-pager">
                      <button
                        className="scm-btn-more"
                        onClick={() => loadAprovados(false)}
                        disabled={aprovLoading || !aprovHasMore}
                        title={aprovHasMore ? "Carregar mais" : "Não há mais itens"}
                      >
                        {aprovLoading ? "Carregando…" : (aprovHasMore ? "VER MAIS" : "FIM DA LISTA")}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Botão Sugerir */}
            <div className="scm-footer">
              <button className="scm-btn-sugerir" onClick={() => setAbrirForm(true)}>
                SUGERIR
              </button>
            </div>
          </>
        )}
      </div>

      {/* Modal interno do formulário */}
      <SugerirFormModal
        isOpen={abrirForm}
        onClose={() => setAbrirForm(false)}
        config={config}
        currentUid={currentUid}
        openMsg={(title, text) => { setMsgTitle(title); setMsgText(text); setMsgOpen(true); }}
      />

      {/* Popup simples (OK) — sem XPopup, para evitar conflito */}
      {msgOpen && (
        <div className="scm-wrap" role="dialog" aria-modal="true">
          <button className="scm-backdrop" onClick={() => setMsgOpen(false)} aria-label="Fechar" />
          <div className="scm-modal scm-modal-small">
            <header className="scm-form-head"><h3>{msgTitle}</h3></header>
            <div><p>{msgText}</p></div>
            <footer className="scm-form-footer">
              <button className="scm-btn-enviar" onClick={() => setMsgOpen(false)}>OK</button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

/* ========== Modal do Formulário (com envio real) ========== */
function SugerirFormModal({ isOpen, onClose, config, currentUid, openMsg }) {
  const premiacoesAtivas = useMemo(() => {
    if (!config?.premiacoesAtivas) return [];
    return Object.entries(config.premiacoesAtivas)
      .filter(([, on]) => !!on)
      .map(([id]) => id);
  }, [config]);

  const [premiacaoId, setPremiacaoId] = useState("");
  const [categoriaId, setCategoriaId] = useState("");

  // Campos dinâmicos
  const campos = useMemo(() => getCamposParaCategoria(categoriaId), [categoriaId]);
  const tipoCategoria = useMemo(() => getTipoCategoria(categoriaId), [categoriaId]);

  // Inputs
  const [titulo, setTitulo] = useState("");
  const [filme, setFilme] = useState("");
  const [pais, setPais] = useState("");
  const [cancao, setCancao] = useState("");
  const [enviando, setEnviando] = useState(false);

  // Reseta categoria ao trocar premiação
  useEffect(() => { setCategoriaId(""); }, [premiacaoId]);

   const categorias = useMemo(() => {
  console.log(">>> premiacaoId selecionado:", premiacaoId); // DEBUG 1
  if (!premiacaoId) return [];

  const keyIndicados = `${premiacaoId}-indicados`;
  const keyVencedores = `${premiacaoId}-vencedores`;

  const listIndicados = getCategoriasOrdenadasDoModo(keyIndicados);
  console.log(">>> categorias (indicados):", keyIndicados, listIndicados); // DEBUG 2

  if (listIndicados && listIndicados.length) return listIndicados;

  const listVencedores = getCategoriasOrdenadasDoModo(keyVencedores);
  console.log(">>> categorias (vencedores):", keyVencedores, listVencedores); // DEBUG 3

  return listVencedores;
}, [premiacaoId]);

  function limpar() {
    setPremiacaoId("");
    setCategoriaId("");
    setTitulo("");
    setFilme("");
    setPais("");
    setCancao("");
  }

  function validarCampos() {
    if (!premiacaoId) return { ok: false, msg: "Selecione a premiação." };
    if (!categoriaId) return { ok: false, msg: "Selecione a categoria." };

    const c = campos;
    if (c === "titulodofilme") {
      if (!titulo.trim()) return { ok: false, msg: "Preencha o nome do filme." };
    } else if (c === "titulodofilme+pais") {
      if (!titulo.trim()) return { ok: false, msg: "Preencha o nome do filme." };
      if (!pais.trim()) return { ok: false, msg: "Preencha o país." };
    } else if (c === "titulodacanção+filme") {
      if (!cancao.trim()) return { ok: false, msg: "Preencha o título da canção." };
      if (!filme.trim()) return { ok: false, msg: "Preencha o nome do filme." };
    } else if (c === "nomedoprofissional+filme") {
      if (!titulo.trim()) return { ok: false, msg: "Preencha o nome do profissional." };
      if (!filme.trim()) return { ok: false, msg: "Preencha o nome do filme." };
    }
    return { ok: true };
  }

  async function handleEnviar(e) {
    e.preventDefault();
    if (enviando) return;

    const v = validarCampos();
    if (!v.ok) {
      openMsg("Formulário incompleto", v.msg);
      return;
    }

    try {
      setEnviando(true);

      // 1 leitura: usuarios/{uid}
      const uref = doc(db, "usuarios", currentUid || (window.__uid || "anon"));
      const usnap = await getDoc(uref);
      if (!usnap.exists()) {
        openMsg("Conta necessária", "Faça login novamente para enviar sugestões.");
        return;
      }
      const udata = usnap.data() || {};
      const isEspecialista = !!udata.especialista;

      // limite de 24h apenas para usuários normais
      if (!isEspecialista) {
        const lastStr = udata.lastSugestaoAt || udata.lastSugestaoAtMs || null;
        let lastMs = 0;
        if (typeof lastStr === "number") lastMs = lastStr;
        else if (typeof lastStr === "string") lastMs = Number(new Date(lastStr)) || 0;

        const nowMs = Date.now();
        if (lastMs && nowMs - lastMs < 24 * 60 * 60 * 1000) {
          openMsg("Você já enviou hoje", "Você já enviou uma sugestão hoje e só pode enviar uma nova daqui 24 horas.");
          return;
        }
      }

      // Monta o payload
      const votosNec = (typeof config?.votosPadrao === "number" && config.votosPadrao > 0) ? config.votosPadrao : 40;
      const base = {
        premiacaoId,
        categoriaId,
        tipoCategoria,
        imagemUrl: "",
        status: "pendente",
        votosNecessarios: votosNec,
        votosCount: 0,
        voters: [],
        aprovadoPor: null,
        especialistaAutor: !!isEspecialista,
        sugeridoPor: {
          uid: currentUid || window.__uid || "",
          nome: (udata.nome && udata.sobrenome) ? `${udata.nome} ${udata.sobrenome}` : (udata.nome || "—"),
          especialista: !!isEspecialista,
        },
        createdAt: serverTimestamp(),
        aprovadoAt: null,
      };

      // Campos dinâmicos
      const c = getCamposParaCategoria(categoriaId);
      if (c === "titulodofilme") {
        base.titulo = titulo.trim();
        base.filme = "";
        base.pais = "";
      } else if (c === "titulodofilme+pais") {
        base.titulo = titulo.trim();
        base.filme = "";
        base.pais = pais.trim();
      } else if (c === "titulodacanção+filme") {
        base.titulo = cancao.trim();
        base.filme = filme.trim();
        base.pais = "";
      } else if (c === "nomedoprofissional+filme") {
        base.titulo = titulo.trim();
        base.filme = filme.trim();
        base.pais = "";
      } else {
        base.titulo = titulo.trim();
        base.filme = "";
        base.pais = "";
      }

      // Cria doc
      await addDoc(collection(db, "sugestoes"), base);

      // Atualiza cooldown
      if (!isEspecialista) {
        await updateDoc(uref, { lastSugestaoAt: Date.now() });
      }

      // Mensagens
      if (isEspecialista) {
        openMsg(
          "Sugestão recebida",
          "Sendo especialista sua sugestão é aprovada automaticamente. A equipe do Termometeiro vai dar prosseguimento em breve."
        );
      } else {
        openMsg(
          "Enviado para aprovação",
          "Sua sugestão foi enviada para aprovação do Termometeiro."
        );
      }

      onClose();
      limpar();
    } catch (err) {
      console.error(err);
      openMsg("Erro ao enviar", "Não foi possível enviar agora. Tente novamente em instantes.");
    } finally {
      setEnviando(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="scm-wrap" role="dialog" aria-modal="true">
      <button className="scm-backdrop" onClick={onClose} aria-label="Fechar" />
      <div className="scm-modal scm-modal-form">
        <div className="scm-form-head">
          <h3>SUGERIR</h3>
          <button className="scm-x" onClick={onClose} aria-label="Fechar">×</button>
        </div>

        {!config?.enabled ? (
          <div className="scm-disabled"><p>Função desativada no momento.</p></div>
        ) : (
          <form className="scm-form" onSubmit={handleEnviar}>
            {/* Premiação */}
            <label className="scm-label">PREMIAÇÃO</label>
            <div className="scm-select-wrap">
              <select value={premiacaoId} onChange={(e) => setPremiacaoId(e.target.value)} required>
                <option value="" disabled>Selecione…</option>
                {premiacoesAtivas.map((id) => (
                  <option key={id} value={id}>{PREMIACOES_LABEL[id] || id}</option>
                ))}
              </select>
            </div>

            {/* Categorias */}
            <label className="scm-label">CATEGORIAS</label>
            <div className="scm-select-wrap">
            <select
                value={categoriaId}
                onChange={(e) => {
                const v = String(e.target.value || "");
                console.log(">>> categoria selecionada:", v);
                setCategoriaId(v);
                }}
                required
                disabled={premiacaoId === ""} // equivalente a !premiacaoId, mais explícito
            >
                <option value="" disabled>Selecione…</option>
                {categorias.map((cat) => (
                <option key={String(cat)} value={String(cat)}>
                    {getNomeBonito(cat) || String(cat)}
                </option>
                ))}
            </select>
            </div>

            {/* Campos dinâmicos */}
            <div className="scm-dyn-fields">
              {campos === "titulodofilme" && (
                <>
                  <label className="scm-label">NOME DO FILME</label>
                  <input value={titulo} onChange={(e) => setTitulo(e.target.value)} required />
                </>
              )}

              {campos === "titulodofilme+pais" && (
                <>
                  <label className="scm-label">NOME DO FILME</label>
                  <input value={titulo} onChange={(e) => setTitulo(e.target.value)} required />
                  <label className="scm-label">PAÍS</label>
                  <input value={pais} onChange={(e) => setPais(e.target.value)} required />
                </>
              )}

              {campos === "titulodacanção+filme" && (
                <>
                  <label className="scm-label">TÍTULO DA CANÇÃO</label>
                  <input value={cancao} onChange={(e) => setCancao(e.target.value)} required />
                  <label className="scm-label">NOME DO FILME</label>
                  <input value={filme} onChange={(e) => setFilme(e.target.value)} required />
                </>
              )}

              {campos === "nomedoprofissional+filme" && (
                <>
                  <label className="scm-label">NOME DO ATOR/ATRIZ OU DIRETOR(A)</label>
                  <input value={titulo} onChange={(e) => setTitulo(e.target.value)} required />
                  <label className="scm-label">NOME DO FILME</label>
                  <input value={filme} onChange={(e) => setFilme(e.target.value)} required />
                </>
              )}
            </div>

            <input type="hidden" value={tipoCategoria} readOnly />

            <div className="scm-form-footer">
              <button type="submit" className="scm-btn-enviar" disabled={enviando}>
                {enviando ? "Enviando…" : "ENVIAR PARA APROVAÇÃO"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
